import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { Mail, Lock, AlertCircle, KeyRound, Fingerprint } from 'lucide-react'
import HCaptcha from '@hcaptcha/react-hcaptcha'

// WebAuthn dispo dans ce navigateur ?
const webAuthnSupported = typeof window !== 'undefined' && !!window.PublicKeyCredential

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(!webAuthnSupported)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRef = useRef<HCaptcha>(null)

  const { signIn, signInWithPasskey } = useAuth()
  const navigate = useNavigate()

  const hcaptchaSiteKey = import.meta.env.VITE_HCAPTCHA_SITEKEY

  const handlePasskeySignIn = async () => {
    setError('')

    if (hcaptchaSiteKey && !captchaToken) {
      setError('Veuillez compléter le captcha.')
      return
    }

    setIsPasskeyLoading(true)

    try {
      await signInWithPasskey(captchaToken || undefined)
      navigate('/dashboard')
    } catch (err) {
      // Le token hCaptcha est à usage unique : consommé par la demande de
      // challenge, il faut le régénérer avant toute nouvelle tentative
      captchaRef.current?.resetCaptcha()
      setCaptchaToken(null)

      // L'utilisateur a annulé la fenêtre WebAuthn : pas d'erreur à afficher
      const name = (err as { name?: string })?.name
      if (name === 'NotAllowedError' || name === 'AbortError') {
        return
      }
      const code = (err as { code?: string })?.code
      if (code === 'passkey_disabled') {
        setError("Les passkeys ne sont pas encore activés sur ce projet.")
      } else if (code === 'webauthn_credential_not_found') {
        setError("Aucun passkey reconnu. Connectez-vous par mot de passe puis enregistrez un passkey dans Paramètres.")
      } else {
        setError('Échec de la connexion par passkey. Réessayez ou utilisez votre mot de passe.')
      }
    } finally {
      setIsPasskeyLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (hcaptchaSiteKey && !captchaToken) {
      setError('Veuillez compléter le captcha.')
      return
    }

    setIsLoading(true)

    try {
      await signIn(email, password, captchaToken || undefined)
      navigate('/dashboard')
    } catch {
      setError('Email ou mot de passe incorrect')
      captchaRef.current?.resetCaptcha()
      setCaptchaToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cream-50 to-beige-100 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-gradient">
            CRM Admin
          </h1>
          <p className="mt-2 text-brown-600">
            Les Sables d'Olonne - Administration
          </p>
        </div>

        {/* Login Card */}
        <Card className="shadow-xl">
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-brown-900">
                Connexion
              </h2>
              <p className="mt-1 text-sm text-brown-500">
                Connectez-vous a votre espace administration
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* hCaptcha — partagé entre passkey et mot de passe */}
            {hcaptchaSiteKey && (
              <div className="flex justify-center">
                <HCaptcha
                  ref={captchaRef}
                  sitekey={hcaptchaSiteKey}
                  onVerify={(token) => { setCaptchaToken(token); setError(''); }}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                />
              </div>
            )}

            {/* Passkey — méthode principale */}
            {webAuthnSupported && (
              <>
                <Button
                  type="button"
                  className="w-full"
                  isLoading={isPasskeyLoading}
                  disabled={!!hcaptchaSiteKey && !captchaToken}
                  onClick={handlePasskeySignIn}
                  leftIcon={<Fingerprint className="w-4 h-4" />}
                >
                  Se connecter avec un passkey
                </Button>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-beige-200" />
                  <span className="text-xs text-brown-400">ou</span>
                  <div className="h-px flex-1 bg-beige-200" />
                </div>

                {!showPasswordForm && (
                  <button
                    type="button"
                    onClick={() => setShowPasswordForm(true)}
                    className="w-full flex items-center justify-center gap-2 text-sm text-brown-500 hover:text-brown-700 transition-colors"
                  >
                    <KeyRound className="w-4 h-4" />
                    Utiliser mon mot de passe
                  </button>
                )}
              </>
            )}

            {/* Formulaire mot de passe — repli */}
            {showPasswordForm && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="email"
                  label="Email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  leftIcon={<Mail className="w-4 h-4" />}
                  required
                />

                <Input
                  type="password"
                  label="Mot de passe"
                  placeholder="Votre mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  leftIcon={<Lock className="w-4 h-4" />}
                  required
                />

                <Button
                  type="submit"
                  variant={webAuthnSupported ? 'secondary' : 'primary'}
                  className="w-full"
                  isLoading={isLoading}
                  disabled={!email || !password || (!!hcaptchaSiteKey && !captchaToken)}
                >
                  Se connecter
                </Button>
              </form>
            )}
          </div>
        </Card>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-brown-500">
          Version 0.1.0
        </p>
      </div>
    </div>
  )
}
